/**
 * Countries and currencies offered during business onboarding.
 *
 * This is a curated list, not an exhaustive ISO dump — onboarding is a dropdown
 * a human reads, and 249 entries is worse than 60. Selecting a country
 * pre-fills the currency and timezone, both of which stay editable.
 *
 * `currencyCode` is ISO-4217, `code` is ISO-3166-1 alpha-2, `timezone` is the
 * IANA zone used for "today" boundaries on the dashboard.
 */

export interface Country {
  code: string;
  name: string;
  currencyCode: string;
  timezone: string;
  dialCode: string;
}

export const COUNTRIES: readonly Country[] = [
  // ── Africa ────────────────────────────────────────────────────────────────
  { code: 'NG', name: 'Nigeria', currencyCode: 'NGN', timezone: 'Africa/Lagos', dialCode: '+234' },
  { code: 'GH', name: 'Ghana', currencyCode: 'GHS', timezone: 'Africa/Accra', dialCode: '+233' },
  { code: 'KE', name: 'Kenya', currencyCode: 'KES', timezone: 'Africa/Nairobi', dialCode: '+254' },
  { code: 'ZA', name: 'South Africa', currencyCode: 'ZAR', timezone: 'Africa/Johannesburg', dialCode: '+27' },
  { code: 'EG', name: 'Egypt', currencyCode: 'EGP', timezone: 'Africa/Cairo', dialCode: '+20' },
  { code: 'TZ', name: 'Tanzania', currencyCode: 'TZS', timezone: 'Africa/Dar_es_Salaam', dialCode: '+255' },
  { code: 'UG', name: 'Uganda', currencyCode: 'UGX', timezone: 'Africa/Kampala', dialCode: '+256' },
  { code: 'RW', name: 'Rwanda', currencyCode: 'RWF', timezone: 'Africa/Kigali', dialCode: '+250' },
  { code: 'CM', name: 'Cameroon', currencyCode: 'XAF', timezone: 'Africa/Douala', dialCode: '+237' },
  { code: 'CI', name: "Côte d'Ivoire", currencyCode: 'XOF', timezone: 'Africa/Abidjan', dialCode: '+225' },
  { code: 'SN', name: 'Senegal', currencyCode: 'XOF', timezone: 'Africa/Dakar', dialCode: '+221' },
  { code: 'ET', name: 'Ethiopia', currencyCode: 'ETB', timezone: 'Africa/Addis_Ababa', dialCode: '+251' },
  { code: 'MA', name: 'Morocco', currencyCode: 'MAD', timezone: 'Africa/Casablanca', dialCode: '+212' },
  { code: 'ZM', name: 'Zambia', currencyCode: 'ZMW', timezone: 'Africa/Lusaka', dialCode: '+260' },
  { code: 'ZW', name: 'Zimbabwe', currencyCode: 'ZWG', timezone: 'Africa/Harare', dialCode: '+263' },
  { code: 'BW', name: 'Botswana', currencyCode: 'BWP', timezone: 'Africa/Gaborone', dialCode: '+267' },

  // ── Europe ────────────────────────────────────────────────────────────────
  { code: 'GB', name: 'United Kingdom', currencyCode: 'GBP', timezone: 'Europe/London', dialCode: '+44' },
  { code: 'IE', name: 'Ireland', currencyCode: 'EUR', timezone: 'Europe/Dublin', dialCode: '+353' },
  { code: 'DE', name: 'Germany', currencyCode: 'EUR', timezone: 'Europe/Berlin', dialCode: '+49' },
  { code: 'FR', name: 'France', currencyCode: 'EUR', timezone: 'Europe/Paris', dialCode: '+33' },
  { code: 'ES', name: 'Spain', currencyCode: 'EUR', timezone: 'Europe/Madrid', dialCode: '+34' },
  { code: 'IT', name: 'Italy', currencyCode: 'EUR', timezone: 'Europe/Rome', dialCode: '+39' },
  { code: 'NL', name: 'Netherlands', currencyCode: 'EUR', timezone: 'Europe/Amsterdam', dialCode: '+31' },
  { code: 'PT', name: 'Portugal', currencyCode: 'EUR', timezone: 'Europe/Lisbon', dialCode: '+351' },
  { code: 'PL', name: 'Poland', currencyCode: 'PLN', timezone: 'Europe/Warsaw', dialCode: '+48' },
  { code: 'TR', name: 'Türkiye', currencyCode: 'TRY', timezone: 'Europe/Istanbul', dialCode: '+90' },

  // ── Americas ──────────────────────────────────────────────────────────────
  { code: 'US', name: 'United States', currencyCode: 'USD', timezone: 'America/New_York', dialCode: '+1' },
  { code: 'CA', name: 'Canada', currencyCode: 'CAD', timezone: 'America/Toronto', dialCode: '+1' },
  { code: 'MX', name: 'Mexico', currencyCode: 'MXN', timezone: 'America/Mexico_City', dialCode: '+52' },
  { code: 'BR', name: 'Brazil', currencyCode: 'BRL', timezone: 'America/Sao_Paulo', dialCode: '+55' },
  { code: 'AR', name: 'Argentina', currencyCode: 'ARS', timezone: 'America/Argentina/Buenos_Aires', dialCode: '+54' },

  // ── Middle East & Asia ────────────────────────────────────────────────────
  { code: 'AE', name: 'United Arab Emirates', currencyCode: 'AED', timezone: 'Asia/Dubai', dialCode: '+971' },
  { code: 'SA', name: 'Saudi Arabia', currencyCode: 'SAR', timezone: 'Asia/Riyadh', dialCode: '+966' },
  { code: 'IN', name: 'India', currencyCode: 'INR', timezone: 'Asia/Kolkata', dialCode: '+91' },
  { code: 'PK', name: 'Pakistan', currencyCode: 'PKR', timezone: 'Asia/Karachi', dialCode: '+92' },
  { code: 'CN', name: 'China', currencyCode: 'CNY', timezone: 'Asia/Shanghai', dialCode: '+86' },
  { code: 'SG', name: 'Singapore', currencyCode: 'SGD', timezone: 'Asia/Singapore', dialCode: '+65' },
  { code: 'MY', name: 'Malaysia', currencyCode: 'MYR', timezone: 'Asia/Kuala_Lumpur', dialCode: '+60' },
  { code: 'ID', name: 'Indonesia', currencyCode: 'IDR', timezone: 'Asia/Jakarta', dialCode: '+62' },
  { code: 'PH', name: 'Philippines', currencyCode: 'PHP', timezone: 'Asia/Manila', dialCode: '+63' },
  { code: 'JP', name: 'Japan', currencyCode: 'JPY', timezone: 'Asia/Tokyo', dialCode: '+81' },

  // ── Oceania ───────────────────────────────────────────────────────────────
  { code: 'AU', name: 'Australia', currencyCode: 'AUD', timezone: 'Australia/Sydney', dialCode: '+61' },
  { code: 'NZ', name: 'New Zealand', currencyCode: 'NZD', timezone: 'Pacific/Auckland', dialCode: '+64' },
];

export interface Currency {
  code: string;
  name: string;
  symbol: string;
}

export const CURRENCIES: readonly Currency[] = [
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: 'GH₵' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£' },
  { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh' },
  { code: 'UGX', name: 'Ugandan Shilling', symbol: 'USh' },
  { code: 'RWF', name: 'Rwandan Franc', symbol: 'FRw' },
  { code: 'XAF', name: 'Central African CFA Franc', symbol: 'FCFA' },
  { code: 'XOF', name: 'West African CFA Franc', symbol: 'CFA' },
  { code: 'ETB', name: 'Ethiopian Birr', symbol: 'Br' },
  { code: 'MAD', name: 'Moroccan Dirham', symbol: 'DH' },
  { code: 'ZMW', name: 'Zambian Kwacha', symbol: 'ZK' },
  { code: 'ZWG', name: 'Zimbabwe Gold', symbol: 'ZiG' },
  { code: 'BWP', name: 'Botswana Pula', symbol: 'P' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
  { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'ARS', name: 'Argentine Peso', symbol: 'AR$' },
  { code: 'PLN', name: 'Polish Złoty', symbol: 'zł' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
];

export const COUNTRY_CODES = COUNTRIES.map((c) => c.code);
export const CURRENCY_CODES = CURRENCIES.map((c) => c.code);

export function findCountry(code: string): Country | undefined {
  return COUNTRIES.find((c) => c.code === code.toUpperCase());
}

export function findCurrency(code: string): Currency | undefined {
  return CURRENCIES.find((c) => c.code === code.toUpperCase());
}

// ── States and regions ──────────────────────────────────────────────────────

export interface Region {
  code: string;
  name: string;
}

/**
 * The first-level divisions of a country, where we have them.
 *
 * Curated per country, for the same reason the country list is — but with a
 * harder requirement than onboarding has: a remittance rate is keyed on what
 * somebody picks here, so free text would quietly split "Enugu" from "enugu "
 * into two rates priced differently.
 *
 * The codes are ours, short and stable. They are what the rate rows store, so
 * they must not be renumbered once a business has priced against them. A
 * country absent from here simply has no state step, and its rates apply
 * country-wide.
 */
export const STATES_BY_COUNTRY: Readonly<Record<string, readonly Region[]>> = {
  NG: [
    { code: 'AB', name: 'Abia' },
    { code: 'AD', name: 'Adamawa' },
    { code: 'AK', name: 'Akwa Ibom' },
    { code: 'AN', name: 'Anambra' },
    { code: 'BA', name: 'Bauchi' },
    { code: 'BY', name: 'Bayelsa' },
    { code: 'BE', name: 'Benue' },
    { code: 'BO', name: 'Borno' },
    { code: 'CR', name: 'Cross River' },
    { code: 'DE', name: 'Delta' },
    { code: 'EB', name: 'Ebonyi' },
    { code: 'ED', name: 'Edo' },
    { code: 'EK', name: 'Ekiti' },
    { code: 'EN', name: 'Enugu' },
    { code: 'FC', name: 'Federal Capital Territory' },
    { code: 'GO', name: 'Gombe' },
    { code: 'IM', name: 'Imo' },
    { code: 'JI', name: 'Jigawa' },
    { code: 'KD', name: 'Kaduna' },
    { code: 'KN', name: 'Kano' },
    { code: 'KT', name: 'Katsina' },
    { code: 'KE', name: 'Kebbi' },
    { code: 'KO', name: 'Kogi' },
    { code: 'KW', name: 'Kwara' },
    { code: 'LA', name: 'Lagos' },
    { code: 'NA', name: 'Nasarawa' },
    { code: 'NI', name: 'Niger' },
    { code: 'OG', name: 'Ogun' },
    { code: 'ON', name: 'Ondo' },
    { code: 'OS', name: 'Osun' },
    { code: 'OY', name: 'Oyo' },
    { code: 'PL', name: 'Plateau' },
    { code: 'RI', name: 'Rivers' },
    { code: 'SO', name: 'Sokoto' },
    { code: 'TA', name: 'Taraba' },
    { code: 'YO', name: 'Yobe' },
    { code: 'ZA', name: 'Zamfara' },
  ],
  GH: [
    { code: 'AH', name: 'Ashanti' },
    { code: 'BO', name: 'Bono' },
    { code: 'CP', name: 'Central' },
    { code: 'EP', name: 'Eastern' },
    { code: 'GA', name: 'Greater Accra' },
    { code: 'NP', name: 'Northern' },
    { code: 'UE', name: 'Upper East' },
    { code: 'UW', name: 'Upper West' },
    { code: 'TV', name: 'Volta' },
    { code: 'WP', name: 'Western' },
  ],
  KE: [
    { code: 'NB', name: 'Nairobi' },
    { code: 'MB', name: 'Mombasa' },
    { code: 'KS', name: 'Kisumu' },
    { code: 'NK', name: 'Nakuru' },
    { code: 'UG', name: 'Uasin Gishu' },
    { code: 'KJ', name: 'Kiambu' },
    { code: 'MC', name: 'Machakos' },
  ],
};

/** The states of one country, or none when we do not carry them. */
export function statesOf(countryCode: string | null | undefined): readonly Region[] {
  if (!countryCode) return [];
  return STATES_BY_COUNTRY[countryCode.toUpperCase()] ?? [];
}

export function findState(
  countryCode: string | null | undefined,
  stateCode: string | null | undefined,
): Region | undefined {
  if (!stateCode) return undefined;
  return statesOf(countryCode).find((s) => s.code === stateCode.toUpperCase());
}

/**
 * What a country calls its first-level divisions, for labels.
 *
 * The divisions themselves are in `STATES_BY_COUNTRY`; this only words them.
 * A country not listed here reads "Region", the neutral term. A country with
 * no divisions listed has no such step at all, whatever it calls them.
 */
export interface RegionTerm {
  singular: string;
  plural: string;
}

const REGION_TERMS: Readonly<Record<string, RegionTerm>> = {
  NG: { singular: 'State', plural: 'States' },
  GH: { singular: 'Region', plural: 'Regions' },
  KE: { singular: 'County', plural: 'Counties' },
  US: { singular: 'State', plural: 'States' },
  ZA: { singular: 'Province', plural: 'Provinces' },
  CA: { singular: 'Province', plural: 'Provinces' },
  GB: { singular: 'Region', plural: 'Regions' },
};

export function regionTermOf(countryCode: string | null | undefined): RegionTerm {
  return (countryCode && REGION_TERMS[countryCode.toUpperCase()]) || { singular: 'Region', plural: 'Regions' };
}
